import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

// Firebase
import {
  addDoc,
  collection,
  deleteDoc,
  getDocs,
  query,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { db } from "../../firebase";
import {
  COLLECTION_NAME,
  REFUND_REQUEST,
  UPDATE_REFUND_REQUEST_STATUS,
} from "../../constants/endpoints";

// Icons
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { FiEye, FiTrash } from "react-icons/fi";
import { BsThreeDotsVertical } from "react-icons/bs";
import { Dropdown, Menu } from "antd";

// Toast
import { Bounce, toast } from "react-toastify";

// Custom Component
import DeleteConfirmationModal from "../../Components/DeleteConfirmationModal/DeleteConfirmationModal";
import Table from "../../Components/Table/Table";
import {
  formatDate,
  formatFirebaseTime,
  firebaseTimeToTimestamp,
} from "../../utils";
import Loader from "../../Components/Loader/Loader";
import PaymentModal2 from "../../Components/PaymentModal2/PaymentModal2";
import { useAuth } from "../../context/AuthContext";
import { getChargeableRideFare } from "../../utils/calculateAllowableRefund";

const RideHistory = () => {
  const { currentUser } = useAuth();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isRefundPaymentOpen, setIsRefundPaymentOpen] = useState(false);
  const [refundTicket, setRefundTicket] = useState(null);
  const [refundFlowBusy, setRefundFlowBusy] = useState(false);
  const [isProcessingRefund, setIsProcessingRefund] = useState(false);
  const [rideToDelete, setRideToDelete] = useState(null);
  const [sortOrder, setSortOrder] = useState("desc");
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [data, setData] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState("all");
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [selectedWeek, setSelectedWeek] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  const getWeekWindow = (timestamp) => {
    const rideDate = new Date(timestamp);
    const day = rideDate.getDay();
    const daysToPreviousSunday = day === 0 ? 7 : day;

    const startDate = new Date(rideDate);
    startDate.setDate(rideDate.getDate() - daysToPreviousSunday);
    startDate.setHours(23, 59, 59, 999);

    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 7);

    return {
      key: `${startDate.getTime()}-${endDate.getTime()}`,
      start: startDate,
      end: endDate,
      label: `${formatDate(startDate)} 11:59 PM - ${formatDate(
        endDate
      )} 11:59 PM`,
    };
  };

  const getEffectiveRideTimestamp = (rideData = {}, createdAtTimestamp = 0) => {
    const isCompleted = rideData?.status === "completed";
    const isCancelled =
      "cancellationStatus" in rideData || !!rideData?.cancellationStatus;

    if (isCompleted) {
      const completedTimestamp = firebaseTimeToTimestamp(rideData?.updatedAt);
      if (Number.isFinite(completedTimestamp) && completedTimestamp > 0) {
        return completedTimestamp;
      }
    }

    if (isCancelled) {
      const cancelledTimestamp = firebaseTimeToTimestamp(rideData?.cancelledAt);
      if (Number.isFinite(cancelledTimestamp) && cancelledTimestamp > 0) {
        return cancelledTimestamp;
      }
    }

    return Number.isFinite(createdAtTimestamp) ? createdAtTimestamp : 0;
  };

  const fetchData = async () => {
    try {
      const ridesSnapshot = await getDocs(
        collection(db, COLLECTION_NAME.ridesHistory)
      );
      const usersSnapshot = await getDocs(
        collection(db, COLLECTION_NAME.users)
      );

      const usersData = usersSnapshot.docs.reduce((acc, doc) => {
        const userData = doc.data();
        acc[doc.id] = userData.fullName || "Unknown User";
        return acc;
      }, {});

      const ridesData = ridesSnapshot.docs
        .map((doc) => {
          const rideData = doc.data();

          const formattedCreatedAt = formatFirebaseTime(rideData.createdAt);
          const createdAtTimestamp = firebaseTimeToTimestamp(
            rideData.createdAt
          );
          const effectiveRideTimestamp = getEffectiveRideTimestamp(
            rideData,
            createdAtTimestamp
          );
          const effectiveRideDate = formatFirebaseTime(effectiveRideTimestamp);

          return {
            id: doc.id,
            ...rideData,
            riderName: usersData[rideData.riderId] || "Unknown Driver",
            pessangerName: usersData[rideData.userId] || "Unknown Passenger",
            createdAt: formattedCreatedAt,
            createdAtTimestamp: createdAtTimestamp,
            effectiveRideDate,
            effectiveRideTimestamp,
          };
        })
        .filter((ride) => {
          // Only show cancelled or completed rides
          const isCancelled = "cancellationStatus" in ride;
          const isCompleted = ride.status === "completed";
          return isCancelled || isCompleted;
        });

      const sortedData = ridesData.sort((a, b) => {
        // Handle invalid timestamps
        const aTime = isNaN(a.effectiveRideTimestamp) ? 0 : a.effectiveRideTimestamp;
        const bTime = isNaN(b.effectiveRideTimestamp) ? 0 : b.effectiveRideTimestamp;

        return sortOrder === "desc" ? bTime - aTime : aTime - bTime;
      });

      setData(sortedData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data: ", error);
    }
  };

  const goToRideDetail = (ride) => {
    navigate(`/rideHistory/${ride.id}`);
  };

  const openDeleteModal = (ride) => {
    setRideToDelete(ride);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setRideToDelete(null);
    setIsDeleteModalOpen(false);
  };

  const closeRefundPaymentModal = () => {
    setRefundTicket(null);
    setIsRefundPaymentOpen(false);
  };

  const updateRefundRequestStatus = async (
    ticket,
    nextStatus,
    updateNote = ""
  ) => {
    const requestId = ticket.doc_id || ticket.id;
    if (!requestId || !nextStatus) return false;

    try {
      const response = await fetch(UPDATE_REFUND_REQUEST_STATUS, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          request_id: requestId,
          status: nextStatus,
          updated_by: currentUser?.email || currentUser?.uid || "admin",
          update_note: updateNote,
        }),
      });

      const rawResponse = await response.text();
      let result = {};
      try {
        result = rawResponse ? JSON.parse(rawResponse) : {};
      } catch {
        result = {};
      }

      if (!response.ok) {
        const fallbackMessage =
          rawResponse && rawResponse.trim().startsWith("<!DOCTYPE")
            ? "Status API returned HTML instead of JSON. Please verify backend route PATCH /refund/request/status and base URL."
            : "Failed to update ticket status.";
        const serverMessage =
          result.message ||
          result.error ||
          (rawResponse ? rawResponse.slice(0, 200) : "");
        throw new Error(
          serverMessage
            ? `${fallbackMessage} (HTTP ${response.status}) ${serverMessage}`
            : `${fallbackMessage} (HTTP ${response.status})`
        );
      }

      toast.success(result.message || "Ticket status updated successfully.", {
        position: "top-right",
        autoClose: 5000,
        transition: Bounce,
      });
      return true;
    } catch (error) {
      console.error("Error updating refund request status:", error);
      toast.error(error.message || "Failed to update ticket status.", {
        position: "top-right",
        autoClose: 5000,
        transition: Bounce,
      });
      return false;
    }
  };

  const executeRefund = async (
    id,
    ride_id,
    reason = "service_issue",
    totalRideFare,
    amount
  ) => {
    setIsProcessingRefund(true);
    try {
      if (!amount || amount > totalRideFare) {
        toast.error(
          "Refund amount must be less than or equal to total ride fare."
        );
        return;
      }

      let refundRequestId = id;
      let refundDocId = refundTicket?.doc_id || id;

      // Create a refund request only when admin confirms submission.
      if (!refundRequestId) {
        const docRef = await addDoc(
          collection(db, COLLECTION_NAME.refundRequests),
          {
            ride_id: ride_id,
            reason,
            description: "Refund request created from Ride History.",
            amount_requested: amount,
            total_ride_fare: totalRideFare,
            created_at: serverTimestamp(),
            created_by: currentUser?.email || currentUser?.uid || "admin",
            status: "pending",
          }
        );
        refundRequestId = docRef.id;
        refundDocId = docRef.id;
      }

      const payload = {
        refund_request_id: refundRequestId,
        ride_id: ride_id,
        refund_reason: reason,
        refund_amount: amount,
      };

      const response = await fetch(REFUND_REQUEST, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.ok) {
        const statusUpdated = await updateRefundRequestStatus(
          refundTicket || { id: refundRequestId, doc_id: refundDocId },
          "completed",
          "Refund initiated by admin"
        );

        if (statusUpdated) {
          toast.success(result.message || "Refund initiated successfully", {
            position: "top-right",
            autoClose: 5000,
            transition: Bounce,
          });
        }
      } else {
        throw new Error(
          result.message || result.error || "Refund failed. Please try again."
        );
      }
    } catch (error) {
      console.error("Refund error:", error);
      toast.error(error.message || "Refund failed. Please try again.", {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setIsProcessingRefund(false);
      closeRefundPaymentModal();
    }
  };

  const openRefundFlow = useCallback(
    async (ride) => {
      if (!ride?.id) return;
      setRefundFlowBusy(true);
      try {
        const snapshot = await getDocs(
          query(
            collection(db, COLLECTION_NAME.refundRequests),
            where("ride_id", "==", ride.id)
          )
        );

        const activeDocs = snapshot.docs.filter((doc) => {
          const status = doc.data().status?.toLowerCase();
          return status !== "declined" && status !== "completed";
        });

        if (activeDocs.length > 0) {
          const docSnap = activeDocs[0];
          const d = docSnap.data();
          setRefundTicket({
            doc_id: docSnap.id,
            id: d.id || docSnap.id,
            ride_id: d.ride_id || d.rideId || ride.id,
            total_ride_fare: d.total_ride_fare ?? d.totalRideFare ?? 0,
          });
          setIsRefundPaymentOpen(true);
          return;
        }

        const totalFare = getChargeableRideFare(ride);
        setRefundTicket({
          doc_id: null,
          id: null,
          ride_id: ride.id,
          total_ride_fare: totalFare,
        });
        setIsRefundPaymentOpen(true);
      } catch (error) {
        console.error("Error starting refund flow:", error);
        toast.error(
          error.message || "Could not start refund. Please try again.",
          {
            position: "top-right",
            autoClose: 5000,
            transition: Bounce,
          }
        );
      } finally {
        setRefundFlowBusy(false);
      }
    },
    []
  );

  const handleDelete = async () => {
    setDeleteLoading(true);
    if (rideToDelete && rideToDelete.id) {
      try {
        const ridesCollection = collection(db, COLLECTION_NAME.ridesHistory);
        const q = query(ridesCollection, where("id", "==", rideToDelete.id));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          querySnapshot.forEach(async (doc) => {
            await deleteDoc(doc.ref);
          });

          toast.success("Ride Deleted Successfully", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            theme: "light",
            transition: Bounce,
          });

          setData((prevData) =>
            prevData.filter((ride) => ride.id !== rideToDelete.id)
          );
        } else {
          toast.error("Ride not found or already deleted.", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            theme: "light",
            transition: Bounce,
          });
        }
      } catch (error) {
        console.error("Error deleting document: ", error);
        toast.error("Failed to delete ride. Please try again.", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "light",
          transition: Bounce,
        });
      } finally {
        setDeleteLoading(false);
        closeDeleteModal();
      }
    }
  };

  const toggleSortOrder = () => {
    setSortOrder((prevOrder) => (prevOrder === "desc" ? "asc" : "desc"));
  };

  const formatCurrency = (value, fallback = "-----") => {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? `$${numberValue.toFixed(2)}` : fallback;
  };

  const SWYFT_SPLIT_CUTOFF_TIMESTAMP = new Date("2025-11-01T00:00:00").getTime();

  const getNumericValue = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const isLegacyRide = (ride) => {
    const ts =
      getNumericValue(ride?.effectiveRideTimestamp) ??
      getNumericValue(ride?.createdAtTimestamp);
    return ts !== null && ts < SWYFT_SPLIT_CUTOFF_TIMESTAMP;
  };

  const getDriverShareAmount = (ride) => {
    const driverShare = getNumericValue(ride?.driverPrice);
    return driverShare !== null ? driverShare : 0;
  };

  const getSwyftShareAmount = (ride) => {
    const storedSwyftShare = getNumericValue(ride?.swyftPrice);
    if (storedSwyftShare !== null) return storedSwyftShare;

    const retainedPrice = getNumericValue(ride?.price);
    const driverShare = getNumericValue(ride?.driverPrice);
    if (retainedPrice === null) return null;

    // For legacy rides (before Nov 2025) the split fields may be missing entirely.
    // Only derive a Swyft share when driverPrice is also stored, otherwise we'd
    // mistakenly report the full retained price as Swyft's share.
    if (isLegacyRide(ride) && driverShare === null) return null;

    return retainedPrice - (driverShare ?? 0);
  };

  const monthOptions = useMemo(() => {
    const monthMap = data.reduce((acc, ride) => {
      const ts = Number(ride.effectiveRideTimestamp);
      if (!Number.isFinite(ts)) return acc;

      const rideDate = new Date(ts);
      const key = `${rideDate.getFullYear()}-${String(
        rideDate.getMonth() + 1
      ).padStart(2, "0")}`;

      if (!acc.has(key)) {
        acc.set(key, {
          value: key,
          label: rideDate.toLocaleString("en-US", {
            month: "long",
            year: "numeric",
          }),
        });
      }
      return acc;
    }, new Map());

    return Array.from(monthMap.values()).sort((a, b) =>
      b.value.localeCompare(a.value)
    );
  }, [data]);

  const monthFilteredData = useMemo(() => {
    if (selectedMonth === "all") return data;

    return data.filter((ride) => {
      const ts = Number(ride.effectiveRideTimestamp);
      if (!Number.isFinite(ts)) return false;
      const rideDate = new Date(ts);
      const monthKey = `${rideDate.getFullYear()}-${String(
        rideDate.getMonth() + 1
      ).padStart(2, "0")}`;
      return monthKey === selectedMonth;
    });
  }, [data, selectedMonth]);

  const weekOptions = useMemo(() => {
    const weekMap = monthFilteredData.reduce((acc, ride) => {
      const ts = Number(ride.effectiveRideTimestamp);
      if (!Number.isFinite(ts)) return acc;
      const weekWindow = getWeekWindow(ts);

      if (!acc.has(weekWindow.key)) {
        acc.set(weekWindow.key, {
          value: weekWindow.key,
          label: weekWindow.label,
          start: weekWindow.start.getTime(),
          end: weekWindow.end.getTime(),
        });
      }
      return acc;
    }, new Map());

    return Array.from(weekMap.values()).sort((a, b) => b.start - a.start);
  }, [monthFilteredData]);

  const weekFilteredData = useMemo(() => {
    if (selectedWeek === "all") return monthFilteredData;

    const [start, end] = selectedWeek.split("-").map(Number);
    return monthFilteredData.filter((ride) => {
      const ts = Number(ride.effectiveRideTimestamp);
      if (!Number.isFinite(ts)) return false;
      return ts > start && ts <= end;
    });
  }, [monthFilteredData, selectedWeek]);

  const driverOptions = useMemo(() => {
    const driverMap = weekFilteredData.reduce((acc, ride) => {
      const riderId = ride?.riderId;
      if (!riderId) return acc;

      if (!acc.has(riderId)) {
        acc.set(riderId, {
          value: riderId,
          label: ride?.riderName || "Unknown Driver",
        });
      }
      return acc;
    }, new Map());

    return Array.from(driverMap.values()).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [weekFilteredData]);

  useEffect(() => {
    if (selectedWeek === "all") return;
    const isSelectedWeekAvailable = weekOptions.some(
      (week) => week.value === selectedWeek
    );

    if (!isSelectedWeekAvailable) {
      setSelectedWeek("all");
    }
  }, [selectedWeek, weekOptions]);

  useEffect(() => {
    if (selectedDriver === "all") return;
    const isSelectedDriverAvailable = driverOptions.some(
      (driver) => driver.value === selectedDriver
    );

    if (!isSelectedDriverAvailable) {
      setSelectedDriver("all");
    }
  }, [selectedDriver, driverOptions]);

  const filteredData = useMemo(() => {
    return monthFilteredData.filter((ride) => {
      const ts = Number(ride.effectiveRideTimestamp);
      if (!Number.isFinite(ts)) return false;

      const matchesDriver =
        selectedDriver === "all" || ride.riderId === selectedDriver;

      let matchesWeek = true;
      if (selectedWeek !== "all") {
        const [start, end] = selectedWeek.split("-").map(Number);
        matchesWeek = ts > start && ts <= end;
      }

      return matchesDriver && matchesWeek;
    });
  }, [monthFilteredData, selectedDriver, selectedWeek]);

  const searchedData = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return filteredData;

    return filteredData.filter((ride) => {
      const searchableFields = [
        ride?.id,
        ride?.riderName,
        ride?.pessangerName,
        ride?.pickupAddress,
        ride?.destinationAddress,
        ride?.status,
        ride?.paymentStatus,
        ride?.effectiveRideDate,
      ];

      return searchableFields.some((field) =>
        String(field || "")
          .toLowerCase()
          .includes(term)
      );
    });
  }, [filteredData, searchTerm]);

  const selectedDriverWeeklyTotal = useMemo(() => {
    if (selectedDriver === "all") return null;

    const total = searchedData.reduce((sum, ride) => {
      if (ride.riderId !== selectedDriver) return sum;
      return sum + getDriverShareAmount(ride) + Number(ride?.tip || 0);
    }, 0);

    return total;
  }, [searchedData, selectedDriver]);

  const weekDriverTotal = useMemo(() => {
    return searchedData.reduce((sum, ride) => {
      return sum + getDriverShareAmount(ride) + Number(ride?.tip || 0);
    }, 0);
  }, [searchedData]);

  const weekSwyftShareTotal = useMemo(() => {
    return searchedData.reduce((sum, ride) => {
      const swyftShare = getSwyftShareAmount(ride);
      return sum + (swyftShare !== null ? swyftShare : 0);
    }, 0);
  }, [searchedData]);

  const columns = useMemo(
    () => [
      { Header: "ID", accessor: "id" },
      { Header: "Driver Name", accessor: "riderName" },
      { Header: "Passenger Name", accessor: "pessangerName" },
      { Header: "Pickup Address", accessor: "pickupAddress" },
      {
        Header: "Destination Address",
        accessor: "destinationAddress",
        Cell: ({ value }) =>
          value && value.length > 40
            ? `${value.slice(0, 40)}...`
            : value || "-----",
      },
      {
        Header: "Original Price",
        accessor: "originalPrice",
        Cell: ({ row }) => {
          const ride = row.original;
          const price = ride?.originalPrice || ride?.price;
          return price ? `$${parseFloat(price).toFixed(2)}` : "-----";
        },
      },
      {
        Header: "Retained Price",
        accessor: "price",
        Cell: ({ value }) => formatCurrency(value),
      },
      {
        Header: "Swyft Share",
        accessor: "swyftPrice",
        Cell: ({ row }) => {
          const ride = row.original;
          const swyftShare = getSwyftShareAmount(ride);
          return swyftShare !== null ? formatCurrency(swyftShare, "$0.00") : "N/A";
        },
      },
      {
        Header: "Driver Share",
        accessor: "driverPrice",
        Cell: ({ row, value }) => {
          if (isLegacyRide(row.original) && getNumericValue(value) === null) {
            return "N/A";
          }
          return formatCurrency(value, "$0.00");
        },
      },
      {
        Header: "Tips",
        accessor: "tip",
        Cell: ({ value }) => formatCurrency(value, "$0.00"),
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ row }) => {
          const ride = row.original;
          if ("cancellationStatus" in ride) {
            return <span>Cancelled</span>;
          }
          return ride.status || "-----";
        },
      },

      { Header: "Payment Status", accessor: "paymentStatus" },
      {
        Header: (
          <div
            onClick={toggleSortOrder}
            className="cursor-pointer flex gap-1 items-center"
          >
            Ride Date{" "}
            {sortOrder === "desc" ? <IoIosArrowDown /> : <IoIosArrowUp />}
          </div>
        ),
        accessor: "effectiveRideDate",
        Cell: ({ value }) => (value ? value : "-----"),
      },
      {
        Header: "Actions",
        accessor: "actions",
        Cell: ({ row }) => {
          const menu = (
            <Menu>
              <Menu.Item
                key="refund"
                disabled={refundFlowBusy}
                onClick={() => openRefundFlow(row.original)}
              >
                Create refund
              </Menu.Item>
            </Menu>
          );

          return (
            <div className="flex gap-2 items-center">
              <button
                className="text-primary hover:text-dazzledBlue text-lg"
                onClick={() => goToRideDetail(row.original)}
              >
                <FiEye />
              </button>
              {/* <button
                className="text-red-600 hover:text-red-900 text-lg"
                onClick={() => openDeleteModal(row.original)}
              >
                <FiTrash />
              </button> */}
              <Dropdown overlay={menu} trigger={["click"]}>
                <button className="text-gray-700 hover:text-black text-lg">
                  <BsThreeDotsVertical />
                </button>
              </Dropdown>
            </div>
          );
        },
      },
    ],
    [sortOrder, openRefundFlow, refundFlowBusy]
  );

  useEffect(() => {
    fetchData();
  }, [sortOrder]);

  return (
    <div className="px-1 sm:px-1 md-px-4 min-h-screen">
      <h1 className="text-2xl md:text-3xl font-bold mt-8 md:mt-5 mb-2 md:mb-5 text-primary">
        Ride History
      </h1>

      {loading ? (
        <Loader />
      ) : (
        data && (
          <div className="">
            <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
              <h2 className="text-base md:text-lg font-semibold text-dazzledBlue mb-3">
                Filter Ride Report
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700">
                    Month
                  </label>
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className="border p-2 rounded-lg border-gray-300 outline-[#0D3D51]"
                  >
                    <option value="all">All Months</option>
                    {monthOptions.map((month) => (
                      <option key={month.value} value={month.value}>
                        {month.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700">Week</label>
                  <select
                    value={selectedWeek}
                    onChange={(e) => setSelectedWeek(e.target.value)}
                    className="border p-2 rounded-lg border-gray-300 outline-[#0D3D51]"
                  >
                    <option value="all">All Weeks</option>
                    {weekOptions.map((week) => (
                      <option key={week.value} value={week.value}>
                        {week.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700">
                    Driver
                  </label>
                  <select
                    value={selectedDriver}
                    onChange={(e) => setSelectedDriver(e.target.value)}
                    className="border p-2 rounded-lg border-gray-300 outline-[#0D3D51]"
                  >
                    <option value="all">All Drivers</option>
                    {driverOptions.map((driver) => (
                      <option key={driver.value} value={driver.value}>
                        {driver.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700">
                    Search
                  </label>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Ride ID, passenger, driver..."
                    className="border p-2 rounded-lg border-gray-300 outline-[#0D3D51]"
                  />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-[#F8FCFF] to-[#EFF7FB] border border-[#D8E8F2] rounded-lg p-3 mb-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <div className="bg-white border border-[#DDEAF2] rounded-lg p-2">
                  <p className="text-xs text-gray-500">
                    Driver Share (Including Tips)
                  </p>
                  <h2 className="text-xl font-bold text-primary leading-tight">
                    {formatCurrency(weekDriverTotal, "$0.00")}
                  </h2>
                </div>
                <div className="bg-white border border-[#DDEAF2] rounded-lg p-2">
                  <p className="text-xs text-gray-500">Swyft Share Total</p>
                  <h2 className="text-xl font-bold text-primary leading-tight">
                    {formatCurrency(weekSwyftShareTotal, "$0.00")}
                  </h2>
                </div>
                <div className="bg-white border border-[#DDEAF2] rounded-lg p-2">
                  <p className="text-xs text-gray-500">Total Rides</p>
                  <h2 className="text-xl font-bold text-primary leading-tight">
                    {searchedData.length}
                  </h2>
                </div>
              </div>

              {selectedDriverWeeklyTotal !== null && (
                <p className="text-xs text-gray-700 mt-2">
                  Selected driver share (including tips):{" "}
                  <span className="font-semibold">
                    {formatCurrency(selectedDriverWeeklyTotal, "$0.00")}
                  </span>
                </p>
              )}
            </div>
            <div>
              <Table
                columns={columns}
                data={searchedData}
                screen={"ridesHistory"}
                hideGlobalFilter={true}
              />
            </div>
            <DeleteConfirmationModal
              isOpen={isDeleteModalOpen}
              onRequestClose={closeDeleteModal}
              onConfirm={handleDelete}
              loading={deleteLoading}
            />
            <PaymentModal2
              isOpen={isRefundPaymentOpen}
              onRequestClose={closeRefundPaymentModal}
              ride={refundTicket}
              isRefundModal
              processing={isProcessingRefund}
              onConfirm={(amount, opts) => {
                const fareCap =
                  opts?.totalRideFareCap ?? refundTicket?.total_ride_fare ?? 0;
                executeRefund(
                  refundTicket?.id,
                  refundTicket?.ride_id,
                  "service_issue",
                  fareCap,
                  amount
                );
              }}
            />
          </div>
        )
      )}
    </div>
  );
};

export default RideHistory;

